import { MapPin, ShieldAlert } from 'lucide-react';
import { ChatMessage } from '@wundo/ui';
import type { Message, MessageBodyType } from '@/types/messages.ts';
import { MessageBody } from './MessageBody.tsx';
import { MessageChoices } from './MessageChoices.tsx';
import { MetadataDump } from './MetadataDump.tsx';

export interface MessageBubbleProps {
  message: Message;
  /** The words to draw: the message's own, or the original the reader asked for. */
  body: MessageBodyType;
  fromUser: boolean;
  /** The debug dump of everything else the message carried; null outside debug. */
  metadata: string | null;
  onButtonClick?: (title: string) => void;
  /** Whether the choices are inert: a recorded answer, or one already on its way. */
  answered: boolean;
}

/** What was said, by the user or by Wundo, in its bubble. */
export function MessageBubble({
  message,
  body,
  fromUser,
  metadata,
  onButtonClick,
  answered,
}: MessageBubbleProps) {
  const anchor = message.meta?.anchor;

  return (
    <ChatMessage
      author={fromUser ? 'user' : 'wundo'}
      authorLabel={fromUser ? 'User' : 'Wundo'}
      bare={body.type === 'notification' && body.delivered?.channel === 'msteams'}
    >
      {message.meta?.content_filtered ? (
        <p className='mb-2 flex items-center gap-1.5 text-xs'>
          <ShieldAlert className='size-3.5 shrink-0' aria-hidden='true' />
          Blocked by content filter
        </p>
      ) : null}

      <MessageBody
        body={body}
        fromUser={fromUser}
        onButtonClick={onButtonClick}
        answered={answered}
      />

      <MessageChoices
        message={message}
        onButtonClick={onButtonClick}
        answered={answered}
      />

      {anchor?.type && anchor.id ? (
        <p className='mt-1.5 flex items-center gap-1 text-xs opacity-75'>
          <MapPin className='size-3.5 shrink-0' aria-hidden='true' />
          {anchor.type} · {anchor.id.slice(0, 8)}
        </p>
      ) : null}

      {metadata && <MetadataDump onPrimary={fromUser}>{metadata}</MetadataDump>}
    </ChatMessage>
  );
}
